import { AgentComponentType } from '@reach/shared-types/agent-components';
import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import type { ApiError } from '../../../lib/api';
import { isVoiceSyncEnqueueFailure } from '../../ai-agents/agent-templates/agent-templates-api';
import { publishProblemsFrom } from '../agent-template-api';
import {
  usePublishTemplate,
  useTemplateComponents,
  useTemplateTenantCount,
  useUnpublishedChanges,
} from '../useTemplateComponents';
import styles from './TemplateSyncStrip.module.css';
import { TOOL_UPDATE_COPY } from './tool-update-copy';
import { UnpublishedChangesDialog } from './UnpublishedChangesDialog';

interface TemplateSyncStripProps {
  templateId: string;
}

interface TemplateStatProps {
  label: string;
  value: number | undefined;
}

export const TemplateSyncStrip = ({ templateId }: TemplateSyncStripProps) => {
  const { data: tenantCount } = useTemplateTenantCount(templateId);
  const { data: templateComponents } = useTemplateComponents(templateId);
  const {
    data: unpublishedChanges,
    error: changesLoadError,
    isFetching: changesAreLoading,
    refetch: refreshChanges,
  } = useUnpublishedChanges(templateId);
  const syncTemplate = usePublishTemplate(templateId);
  const [reviewIsOpen, setReviewIsOpen] = useState(false);
  const [deliveryNeedsRetry, setDeliveryNeedsRetry] = useState(false);

  const changes = unpublishedChanges?.changes ?? [];
  const toolChanges = unpublishedChanges?.toolChanges ?? [];
  const templateHasChanges = changes.length > 0 || toolChanges.length > 0;
  const syncIsAvailable =
    Boolean(unpublishedChanges && (templateHasChanges || deliveryNeedsRetry)) &&
    !syncTemplate.isPending &&
    !changesAreLoading;
  const readySyncLabel =
    deliveryNeedsRetry && !templateHasChanges ? TOOL_UPDATE_COPY.retry : TOOL_UPDATE_COPY.sync;
  const syncLabel = syncTemplate.isPending ? 'Syncing…' : readySyncLabel;
  const syncProblems = syncTemplate.error ? publishProblemsFrom(syncTemplate.error) : [];

  const countOfType = (type: AgentComponentType) =>
    templateComponents?.filter(component => component.type === type).length;

  const showLatestChanges = async () => {
    const { isSuccess } = await refreshChanges();
    if (!isSuccess) return;
    syncTemplate.reset();
    setReviewIsOpen(true);
  };

  const sync = () =>
    syncTemplate.mutate(
      { expectedReviewFingerprint: unpublishedChanges?.reviewFingerprint },
      {
        onSuccess: () => {
          setDeliveryNeedsRetry(false);
          setReviewIsOpen(false);
        },
        onError: error => {
          const templateChangedSinceLoad = (error as ApiError).response?.status === 409;
          if (templateChangedSinceLoad) void showLatestChanges();
          if (isVoiceSyncEnqueueFailure(error)) {
            setDeliveryNeedsRetry(true);
            void refreshChanges();
          }
        },
      },
    );

  return (
    <section className={styles.templateSyncStrip} aria-label="Template sync">
      <dl>
        <TemplateStat label="Tenants using it" value={tenantCount} />
        <TemplateStat label="Skills" value={countOfType(AgentComponentType.SKILL)} />
        <TemplateStat label="Guidance" value={countOfType(AgentComponentType.GUIDANCE)} />
        <TemplateStat label="Escalations" value={countOfType(AgentComponentType.ESCALATION)} />
      </dl>
      {templateHasChanges && (
        <p className={styles.unsyncedNotice}>
          <span>{TOOL_UPDATE_COPY.unsynced}</span>
          <Button type="button" variant="link" size="sm" onClick={() => setReviewIsOpen(true)}>
            View changes
          </Button>
        </p>
      )}
      <Button type="button" disabled={!syncIsAvailable} onClick={sync}>
        {syncLabel}
      </Button>
      {syncProblems.length > 0 && (
        <div role="alert">
          {!deliveryNeedsRetry && <p>This template can't be synced yet:</p>}
          <ul>
            {syncProblems.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}
      {changesLoadError && <p role="alert">{changesLoadError.message}</p>}
      {reviewIsOpen && unpublishedChanges && (
        <UnpublishedChangesDialog
          changes={unpublishedChanges.changes}
          toolChanges={unpublishedChanges.toolChanges ?? []}
          onClose={() => setReviewIsOpen(false)}
        />
      )}
    </section>
  );
};

const TemplateStat = ({ label, value }: TemplateStatProps) => (
  <div>
    <dt>{label}</dt>
    <dd>{value ?? '–'}</dd>
  </div>
);
